import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { 
    FiUser, FiLock, FiArrowRight, FiArrowLeft, FiGlobe, 
    FiCheckCircle, FiShield, FiBriefcase 
} from 'react-icons/fi';
import { BsShieldLock } from 'react-icons/bs';

const RegisterClub = () => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Step 1 Data
    const [verificationData, setVerificationData] = useState({
        admin_identifier: '',
        security_key: ''
    });

    // Step 2 Data
    const [provisioningData, setProvisioningData] = useState({
        lion_ID: '',
        club_name: '',
        club_category: 'Alpha',
        club_type: 'School',
        admin_name: '',
        admin_email: '',
        admin_password: ''
    });

    const [subDistrictInfo, setSubDistrictInfo] = useState(null);

    const handleVerify = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/auth.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'verify_district', ...verificationData }),
            });

            const data = await response.json();
            if (data.success) {
                setSubDistrictInfo(data.data);
                setStep(2);
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toast.error('Failed to connect to verification service');
        } finally {
            setLoading(false);
        }
    };

    const handleProvision = async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = {
            action: 'provision_club',
            lion_ID: provisioningData.lion_ID,
            sub_district_id: subDistrictInfo.sub_district_id,
            club_name: provisioningData.club_name,
            club_category: provisioningData.club_category,
            club_type: provisioningData.club_type,
            admin_name: provisioningData.admin_name,
            admin_email: provisioningData.admin_email,
            admin_password: provisioningData.admin_password
        };

        try {
            const response = await fetch(`${API_BASE_URL}/auth.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Club Provisioned Successfully! Redirecting to login...');
                navigate('/login');
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toast.error('Failed to provision club');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-white font-sans text-slate-800">
            {/* Left Column - Context Presentation */}
            <div className="hidden lg:flex lg:w-[45%] bg-[#F4F7FA] p-12 xl:p-20 flex-col justify-between relative overflow-hidden">
                {/* Background subtle elements */}
                <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-blue-100/40 rounded-full blur-[100px] pointer-events-none"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-slate-200/50 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="z-10 w-full relative">
                    {/* Big Logo Design */}
                    <div className="mb-10 flex items-center">
                        <img 
                            src="/club.png" 
                            alt="Leo Club Logo" 
                            className="h-16 xl:h-20 w-auto object-contain drop-shadow-md transform transition-transform duration-700 hover:scale-105" 
                        />
                    </div>

                    <div className="inline-block px-4 py-1.5 bg-white rounded-full border border-slate-200 text-[9px] font-bold tracking-widest text-slate-500 mb-8 shadow-sm">
                        ADMINISTRATIVE PORTAL
                    </div>

                    <div className="mb-6">
                        <h1 className="text-4xl xl:text-5xl font-bold mb-2 text-slate-800 tracking-tight">
                            Partner with
                        </h1>
                        <h1 className="text-4xl xl:text-5xl font-bold text-slate-400 tracking-tight">
                            MD 306
                        </h1>
                    </div>
                    
                    <p className="text-slate-500 text-sm leading-relaxed max-w-sm font-light mb-16">
                        Securely provision new club entities and assign presidential access credentials in a verified environment.
                    </p>

                    {/* Step Indicators */}
                    <div className="space-y-8 pl-2 w-full max-w-[280px]">
                        {/* Step 1 */}
                        <div className="flex items-center gap-5">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${step === 1 ? 'bg-[#1A1F2B] text-white shadow-lg' : 'bg-white border border-slate-200 text-slate-400'}`}>
                                {step > 1 ? <FiCheckCircle className="text-xl text-[#2A85FF]" /> : <FiShield className="text-xl" />}
                            </div>
                            <div>
                                <h4 className={`text-[10px] font-bold tracking-widest mb-1 ${step >= 1 ? 'text-slate-900' : 'text-slate-400'}`}>STEP 01</h4>
                                <p className={`text-[13px] ${step >= 1 ? 'text-slate-500' : 'text-slate-400'}`}>Identity Verification</p>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex items-center gap-5">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${step === 2 ? 'bg-[#1A1F2B] text-white shadow-lg' : 'bg-transparent border border-slate-300 text-slate-300'}`}>
                                <FiBriefcase className="text-xl" />
                            </div>
                            <div>
                                <h4 className={`text-[10px] font-bold tracking-widest mb-1 ${step === 2 ? 'text-slate-900' : 'text-slate-400'}`}>STEP 02</h4>
                                <p className={`text-[13px] ${step === 2 ? 'text-slate-500' : 'text-slate-400'}`}>Entity Provisioning</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="z-10 flex items-center gap-2 text-[10px] font-bold tracking-widest text-slate-400 uppercase mt-20">
                    <BsShieldLock className="text-sm" /> SECURE SSL CONNECTION
                </div>
            </div>

            {/* Right Column - Form */}
            <div className="w-full lg:w-[55%] flex justify-center p-6 sm:p-12 xl:p-20 relative overflow-y-auto min-h-screen items-center bg-white">
                {/* Mobile Top Brand (only visible on small screens) */}
                <div className="absolute top-8 left-8 flex lg:hidden items-center gap-2 text-xs font-bold tracking-widest text-[#2A85FF]">
                    <FiGlobe /> LEO PORTAL
                </div>

                <div className="w-full max-w-[480px]">
                    {step === 1 ? (
                        <div className="animate-fade-in">
                            <div className="mb-10">
                                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 tracking-tight">Verify Authorization</h2>
                                <p className="text-[13px] text-slate-500 font-light">Enter your district-issued credentials to proceed.</p>
                            </div>

                            <form onSubmit={handleVerify} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-2">
                                        Admin Identifier
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            required
                                            value={verificationData.admin_identifier}
                                            onChange={(e) => setVerificationData({ ...verificationData, admin_identifier: e.target.value })}
                                            className="w-full pl-5 pr-12 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none text-slate-800 font-medium text-[14px] placeholder-slate-300"
                                            placeholder="e.g. 306A1-ADMIN"
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none text-slate-300 group-focus-within:text-slate-500 transition-colors">
                                            <FiUser className="text-lg" />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2 pt-2">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">
                                            Security Key
                                        </label>
                                        <a href="#" className="text-[9px] font-semibold tracking-wider text-slate-400 hover:text-slate-700 transition-colors uppercase">
                                            Lost key?
                                        </a>
                                    </div>
                                    <div className="relative group">
                                        <input
                                            type="password"
                                            required
                                            value={verificationData.security_key}
                                            onChange={(e) => setVerificationData({ ...verificationData, security_key: e.target.value })}
                                            className="w-full pl-5 pr-12 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none text-slate-800 font-medium text-sm placeholder-slate-300 tracking-widest"
                                            placeholder="••••••••••••"
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-5 flex items-center pointer-events-none text-slate-300 group-focus-within:text-slate-500 transition-colors">
                                            <FiLock className="text-lg" />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-8 space-y-6">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-[#141822] hover:bg-black text-white font-bold text-[11px] uppercase tracking-[0.15em] py-4 rounded-xl flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-xl hover:shadow-slate-900/10 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed group"
                                    >
                                        {loading ? 'AUTHENTICATING...' : 'AUTHENTICATE ACCESS'}
                                        {!loading && <FiArrowRight className="text-base text-slate-400 group-hover:text-white transition-transform duration-300 group-hover:translate-x-1" />}
                                    </button>
                                    
                                    <div className="text-center pt-2">
                                        <button
                                            type="button"
                                            onClick={() => navigate('/login')}
                                            className="text-[11px] font-bold text-slate-400 hover:text-slate-800 transition-colors"
                                        >
                                            Return to Login
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="animate-fade-in w-full text-slate-800">
                            <div className="mb-10">
                                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 tracking-tight">Entity Provisioning</h2>
                                <p className="text-[13px] text-slate-500 font-light">Configure Tactical Unit Assets for <strong className="font-semibold text-slate-700">{subDistrictInfo?.sub_district_name}</strong>.</p>
                            </div>

                            <form onSubmit={handleProvision} className="space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">Lion ID</label>
                                        <input
                                            type="number"
                                            required
                                            className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none font-medium text-slate-800 text-[14px]"
                                            placeholder="Unique ID"
                                            value={provisioningData.lion_ID}
                                            onChange={(e) => setProvisioningData({ ...provisioningData, lion_ID: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">District</label>
                                        <input
                                            type="text"
                                            disabled
                                            className="w-full px-5 py-4 bg-slate-50/50 border border-slate-100 rounded-xl text-slate-400 font-medium text-[14px] cursor-not-allowed"
                                            value={subDistrictInfo?.sub_district_name || ''}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">Core Club Name</label>
                                    <div className="relative">
                                        <span className="absolute left-5 top-1/2 -translate-y-1/2 text-[14px] font-medium text-slate-400 pointer-events-none">Leo Club of</span>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-5 py-4 pl-[105px] bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none font-medium text-slate-800 text-[14px]"
                                            placeholder="Location/Identity"
                                            value={provisioningData.club_name.replace(/^Leo Club of\s+/i, '')}
                                            onChange={(e) => setProvisioningData({ ...provisioningData, club_name: `Leo Club of ${e.target.value.trim()}` })}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">Category</label>
                                        <select
                                            className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none font-medium text-slate-800 text-[14px]"
                                            value={provisioningData.club_category}
                                            onChange={(e) => {
                                                const category = e.target.value;
                                                setProvisioningData({
                                                    ...provisioningData,
                                                    club_category: category,
                                                    club_type: category === 'Alpha' ? 'School' : (provisioningData.club_type === 'School' ? 'Community' : provisioningData.club_type)
                                                });
                                            }}
                                        >
                                            <option value="Alpha">Alpha</option>
                                            <option value="Omega">Omega</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">Tactical Type</label>
                                        <select
                                            className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none font-medium text-slate-800 text-[14px] disabled:opacity-60"
                                            value={provisioningData.club_type}
                                            disabled={provisioningData.club_category === 'Alpha'}
                                            onChange={(e) => setProvisioningData({ ...provisioningData, club_type: e.target.value })}
                                        >
                                            {provisioningData.club_category === 'Alpha' ? (
                                                <option value="School">School</option>
                                            ) : (
                                                <>
                                                    <option value="Community">Community</option>
                                                    <option value="University">University</option>
                                                </>
                                            )}
                                        </select>
                                    </div>
                                </div>

                                <div className="mt-8 pt-8 border-t border-slate-100 space-y-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[10px] font-bold tracking-[0.2em] text-[#2A85FF] uppercase">Presidential Account</span>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">Full Name</label>
                                        <div className="relative group">
                                            <input
                                                type="text"
                                                required
                                                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none font-medium text-slate-800 text-[14px]"
                                                placeholder="John Doe"
                                                value={provisioningData.admin_name}
                                                onChange={(e) => setProvisioningData({ ...provisioningData, admin_name: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div className="space-y-2">
                                            <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">System Email</label>
                                            <input
                                                type="email"
                                                required
                                                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none font-medium text-slate-800 text-[14px]"
                                                placeholder="president@domain.com"
                                                value={provisioningData.admin_email}
                                                onChange={(e) => setProvisioningData({ ...provisioningData, admin_email: e.target.value })}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="block text-[10px] font-bold tracking-widest text-slate-500 uppercase">Access Password</label>
                                            <input
                                                type="password"
                                                required
                                                className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:border-slate-300 focus:ring-4 focus:ring-slate-100 transition-all outline-none font-medium text-slate-800 text-[14px] tracking-widest"
                                                placeholder="••••••••"
                                                value={provisioningData.admin_password}
                                                onChange={(e) => setProvisioningData({ ...provisioningData, admin_password: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-slate-100 w-full mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setStep(1)}
                                        className="sm:flex-1 bg-white hover:bg-slate-50 text-slate-500 border border-slate-200 font-bold text-[11px] uppercase tracking-widest py-4 rounded-xl flex items-center justify-center gap-2 transition-all order-2 sm:order-1"
                                    >
                                        <FiArrowLeft className="text-sm" /> BACK
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="sm:flex-[2] bg-[#141822] hover:bg-black text-white font-bold text-[11px] uppercase tracking-widest py-4 rounded-xl flex items-center justify-center gap-3 transition-all group disabled:opacity-50 order-1 sm:order-2 shadow-xl shadow-slate-900/10 hover:-translate-y-0.5"
                                    >
                                        {loading ? 'DEPLOYING...' : 'CONFIRM & DEPLOY'}
                                        <FiCheckCircle className="text-base group-hover:scale-110 transition-transform" />
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegisterClub;
